import React, { useState, useRef, useMemo } from 'react';
import { useI18n } from '../../context/I18nContext';
import { getSezioniArray, getCableHeightForPreview } from '../../utils/cableLogic';
import useDropdown from '../../hooks/useDropdown';
import useIsMobile from '../../hooks/useIsMobile';
import CableColorRect from '../ui/CableColorRect';

const SectionSelector = ({ value, onChange, useAWG, currentColor, className = "" }) => {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const sezioni = getSezioniArray(useAWG);
  const searchInputRef = useRef(null);
  const isMobile = useIsMobile();

  const { isOpen, toggleDropdown, closeDropdown, dropdownRef } = useDropdown(false, {
    autoFocus: searchInputRef,
    onClose: () => setSearchTerm('')
  });

  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return sezioni;

    return sezioni.filter(sez =>
      sez.val.toString().includes(searchTerm.toLowerCase()) ||
      `${sez.val}${useAWG ? ' AWG' : 'mm²'}`.includes(searchTerm.toLowerCase())
    );
  }, [sezioni, searchTerm, useAWG]);

  const handleSectionSelect = (sectionValue) => {
    onChange(sectionValue);
    closeDropdown();
  };

  // Funzione per calcolare l'altezza della preview basata sulla sezione
  const getSectionHeight = (sectionValue, maxHeight = 24) => {
    return getCableHeightForPreview(
      sectionValue, 
      useAWG, 
      isMobile ? 16 : maxHeight, 
      sezioni
    );
  };

  // Funzione per larghezza FISSA dell'anteprima
  const getSectionWidth = () => {
    return '48px'; // Larghezza fissa invece che proporzionale
  };

  // Renderizza preview della sezione con colore
  const renderSectionPreview = (sectionValue, isSelected = false) => {
    const height = getSectionHeight(sectionValue, isMobile ? 16 : 20);
    const width = getSectionWidth();

    return (
      <div className="flex items-center gap-3">
        <CableColorRect
          color={currentColor}
          width={width}
          height={height}
          borderRadius="9999px"
          className="transition-all duration-200 flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="text-base">
            {useAWG ? `${sectionValue} AWG` : `${sectionValue} mm²`}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Pulsante principale */}
      <button
        type="button"
        onClick={toggleDropdown}
        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between min-h-[44px]"
      >
        <div className="flex items-center gap-3">
          <CableColorRect
            color={currentColor}
            width={getSectionWidth()}
            height={getSectionHeight(value, isMobile ? 16 : 20)}
            borderRadius="9999px"
            className="transition-all duration-200 flex-shrink-0"
          />

          <div className="flex-1 min-w-0">
          <div className="text-base">
            {useAWG ? `${value} AWG` : `${value} mm²`}
          </div>
          </div>
        </div>

        <svg
          className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {/* Campo di ricerca */}
          <div className="p-2 border-b">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={useAWG ? t('selectors.searchAWG') : t('selectors.searchSection')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded focus:ring-1 focus:ring-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && filteredSections.length > 0) {
                  handleSectionSelect(filteredSections[0].val);
                }
                e.stopPropagation();
              }}
            />
          </div>

          {/* Lista opzioni - ALTEZZA UGUALE AL COLORE */}
          <div className="max-h-48 overflow-auto">
            {filteredSections.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                {useAWG ? t('selectors.noAWGFound') : t('selectors.noSectionFound')}
              </div>
            ) : (
              filteredSections.map((sez) => (
                <button
                  key={sez.val}
                  type="button"
                  onClick={() => handleSectionSelect(sez.val)}
                  className={`w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none transition-colors ${value === sez.val ? 'bg-blue-100' : ''
                    }`}
                  role="option"
                  aria-selected={value === sez.val}
                >
                  {renderSectionPreview(sez.val, value === sez.val)}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionSelector;

